/**
 * Protocolo postMessage del SDK Finva — CONTRATO CONGELADO (§3.1).
 *
 * Lado HOST del SDK (bundle vanilla esbuild). El lado iframe del contrato vive en
 * `lib/sdk-embed/types.ts` (app Next). Ambos archivos están en este mismo repo
 * pero en contextos de build distintos (esbuild vs Next) y no pueden compartir
 * módulo, así que se duplican a propósito: DEBEN mantenerse idénticos; cualquier
 * cambio aquí se replica allá.
 *
 * Reglas:
 *  - Host → iframe: `finva:init` / `finva:update`.
 *  - iframe → host: `finva:ready` / `finva:submit` / `finva:approved` / `finva:error`
 *                  / `finva:resize` / `finva:close`.
 *  - `SubmitData` está SANITIZADO: sin PII cruda (email/telefono/CURP/domicilio).
 *  - Todo mensaje valida `event.origin` en ambos lados.
 */

export type FinvaMode = 'embedded' | 'modal';
export type FinvaTheme = 'light' | 'dark';

/**
 * Producto que el comercio host quiere financiar. El host lo pasa vía
 * `initialization.product` y viaja al iframe como query params + en
 * `finva:init`. El iframe renderiza calculador+wizard para este producto
 * (no hay catálogo server-side: el host es quien conoce la moto).
 */
export interface FinvaProduct {
  brand: string; // marca de la moto, ej. "yamaha"
  model?: string; // modelo/nombre
  slug?: string;
  year?: number; // modelo-año (ej. 2026)
  /**
   * Precio contado (MXN). Opcional: si el comercio no fija una moto
   * (`finvaMotorcycleId`), el iframe abre el selector de catálogo y el
   * precio sale de ahí. Con moto fija, el catálogo también manda.
   */
  price?: number;
  finvaMotorcycleId?: string; // id interno en Finva (mapeo a /add_solicitud)
  downPayment?: number; // enganche (opcional; si no, el calculador sugiere)
  months?: number; // plazo (opcional; debe estar en TERMS)
}

export interface InitPayload {
  host: string; // origin de la página que embebe el widget (identifica al tenant)
  brand?: string; // marca/tenant del comercio (tema/logo), distinta de product.brand
  mode: FinvaMode;
  theme: FinvaTheme;
  primaryColor?: string;
  secondaryColor?: string;
  locale: string; // default 'es-MX'
  prefill?: { nombre?: string; telefono?: string; email?: string };
  product?: FinvaProduct;
}

/** Comparación estructural de productos (para decidir recarga del iframe en update). */
export function productEqual(a?: FinvaProduct, b?: FinvaProduct): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.brand === b.brand &&
    a.model === b.model &&
    a.slug === b.slug &&
    a.year === b.year &&
    a.price === b.price &&
    a.finvaMotorcycleId === b.finvaMotorcycleId &&
    a.downPayment === b.downPayment &&
    a.months === b.months
  );
}

export type UpdatePayload = Partial<
  Pick<InitPayload, 'theme' | 'primaryColor' | 'secondaryColor' | 'brand' | 'prefill'>
>;

/** Payload sanitizado — SIN PII cruda. */
export interface SubmitData {
  motorcycle: { brand: string; model: string; year: number; price: number };
  quote: { downPayment: number; months: number; monthly: number };
  contact: { emailMasked: string; phoneLast4: string };
}

export interface FinvaError {
  message: string;
  code?: string;
}

export type HostToIframe =
  | { type: 'finva:init'; payload: InitPayload }
  | { type: 'finva:update'; payload: UpdatePayload };

export type IframeToHost =
  | { type: 'finva:ready' }
  | { type: 'finva:submit'; payload: { data: SubmitData } }
  | { type: 'finva:approved'; payload: { solicitud: string } } // string simple por ahora
  | { type: 'finva:error'; payload: { error: FinvaError } }
  | { type: 'finva:resize'; payload: { height: number } }
  | { type: 'finva:close' };